import { OnInit, } from '@angular/core';
import { AppStateFacade, Setting, } from '@app-state';
import { UiSettingForm, } from '@components';
import { FeatureFlag, } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { MailModalComponent } from '@pages/settings/mail.modal';
import { ModalService, SettingService, } from '@services';
import { CalendarService } from '@services/calendar.service';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import cloneDeep from 'lodash-es/cloneDeep';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, forkJoin, of, } from 'rxjs';
import { finalize, map, switchMap, tap, } from 'rxjs/operators';
var EMPTY_SETTING_FORM = {
    formGroup: {},
};
export var TabLabel;
(function (TabLabel) {
    TabLabel["General"] = "General";
    TabLabel["Deployment"] = "Deployment";
    TabLabel["Mail"] = "Mail";
    TabLabel["Security"] = "Security";
    TabLabel["Scalability"] = "Scalability";
    TabLabel["NonWorkingDays"] = "Non-Working Days";
})(TabLabel || (TabLabel = {}));
var SettingsPageComponent = /** @class */ (function () {
    function SettingsPageComponent(settingService, _calendarService, _translateService, _modalService, _snack, _appState) {
        this.settingService = settingService;
        this._calendarService = _calendarService;
        this._translateService = _translateService;
        this._modalService = _modalService;
        this._snack = _snack;
        this._appState = _appState;
        this.loading$ = new BehaviorSubject(true);
        this.creatingCalendar$ = new BehaviorSubject(false);
        this.selectedCalendarIndex = 0;
        this.activeTab = 'General';
        this.permission = 'Settings.Edit';
        this.TabLabel = TabLabel;
        this._smtpTestResult = "settings_mail_not_tested" /* NotTested */;
        this._forms = {};
    }
    Object.defineProperty(SettingsPageComponent.prototype, "general", {
        get: function () {
            return this._forms['general'] || EMPTY_SETTING_FORM;
        },
        set: function (ref) {
            if (!this._forms['general'] && !!ref) {
                this._forms['general'] = ref;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "deployment", {
        get: function () {
            return this._forms['deployment'] || EMPTY_SETTING_FORM;
        },
        set: function (ref) {
            if (!this._forms['deployment'] && !!ref) {
                this._forms['deployment'] = ref;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "mail", {
        get: function () {
            return this._forms['mail'] || EMPTY_SETTING_FORM;
        },
        set: function (ref) {
            if (!this._forms['mail'] && !!ref) {
                this._forms['mail'] = ref;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "security", {
        get: function () {
            return this._forms['security'] || EMPTY_SETTING_FORM;
        },
        set: function (ref) {
            if (!this._forms['security'] && !!ref) {
                this._forms['security'] = ref;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "scalability", {
        get: function () {
            return this._forms['scalability'] || EMPTY_SETTING_FORM;
        },
        set: function (ref) {
            if (!this._forms['scalability'] && !!ref) {
                this._forms['scalability'] = ref;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "skippedDays", {
        get: function () {
            return this._forms['skippedDays'] || EMPTY_SETTING_FORM;
        },
        set: function (ref) {
            if (!this._forms['skippedDays'] && !!ref) {
                this._forms['skippedDays'] = ref;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "invalid", {
        get: function () {
            return this.general.formGroup.invalid ||
                this.deployment.formGroup.invalid ||
                this.mail.formGroup.invalid ||
                this.security.formGroup.invalid ||
                this.scalability.formGroup.invalid ||
                (!this.isMultipleCalendarsEnabled && this.skippedDays.formGroup.invalid);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "pristine", {
        get: function () {
            return this.general.formGroup.pristine &&
                this.deployment.formGroup.pristine &&
                this.mail.formGroup.pristine &&
                this.security.formGroup.pristine &&
                this.scalability.formGroup.pristine &&
                (this.isMultipleCalendarsEnabled || this.skippedDays.formGroup.pristine);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "hasUnsavedChanges", {
        get: function () {
            return !this.pristine;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "hasInvalidCalendars", {
        get: function () {
            var skippedDaysComponent = this.skippedDays;
            return skippedDaysComponent &&
                skippedDaysComponent.calendarItems &&
                skippedDaysComponent.calendarItems.some(function (item) { return item.formGroup.invalid; });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SettingsPageComponent.prototype, "hasUnsavedCalendars", {
        get: function () {
            var skippedDaysComponent = this.skippedDays;
            return skippedDaysComponent &&
                skippedDaysComponent.calendarItems &&
                skippedDaysComponent.calendarItems.some(function (item) { return item.formGroup.dirty; });
        },
        enumerable: true,
        configurable: true
    });
    SettingsPageComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.isWindowsAuthEnabled = this._appState.settings.get(Setting.isWindowsAuthEnabled);
        this.isMultipleCalendarsEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.MultipleNonWorkingCalendars);
        var calendars$ = this.isMultipleCalendarsEnabled ?
            this._calendarService.get({ orderBy: ['Name'] }) :
            this.settingService.getCalendar()
                .pipe(map(function (calendar) { return ({
                total: 1,
                data: [calendar],
            }); }));
        forkJoin({
            settings: this.settingService.getServerSettings(),
            calendars: calendars$,
        }).pipe(finalize(function () { return _this.loading$.next(false); })).subscribe(function (_a) {
            var settings = _a.settings, calendars = _a.calendars;
            _this.settings = settings;
            _this.excludedCalendars = calendars.data || [];
        });
    };
    SettingsPageComponent.prototype.openModal = function () {
        var _this = this;
        // SMTP password is not provided by backend. A dummy one is provided instead.
        // Needs customer to provide a password. Otherwise, the test will always fail even if no settings are changed.
        if (this.settings.mail.smtpPassword === '••••••••••••') {
            this._snack.warning(this._translateService.instant('settings_mail_needs_password_warning'));
            return;
        }
        this._modalService.openModal(MailModalComponent, this.settings.mail)
            .afterClosedResult()
            .subscribe(function (res) {
            switch (res.type) {
                case 4 /* Succeeded */:
                    _this._smtpTestResult = "settings_mail_test_succeeded" /* Succeeded */;
                    _this._snack.success(_this._translateService.instant('settings_mail_test_succeeded', {
                        sendTo: res.additionalData,
                    }));
                    break;
                case 5 /* Failed */:
                    _this._smtpTestResult = "settings_mail_test_failed" /* Failed */;
                    break;
            }
        });
    };
    SettingsPageComponent.prototype.onSubmit = function () {
        var _this = this;
        var enableFolders = this.general.formGroup.get('isModernFolderSystemEnabled');
        var enableFoldersDirty = enableFolders.dirty;
        if (!enableFoldersDirty || !this.settings.general.isModernFolderSystemEnabled) {
            this._checkMailSettings();
        }
        else {
            this._modalService.onConfirm(null, function () { return _this._checkMailSettings(); }, function () {
                enableFolders.setValue(_this.settings.general.isModernFolderSystemEnabled = false);
                _this._checkMailSettings();
            }, this._translateService.instant('settings_general_enable_modern_sure'), true);
        }
    };
    SettingsPageComponent.prototype.onSmtpSettingChanged = function (smtpSettingChanged) {
        this._smtpTestResult = smtpSettingChanged ? "settings_mail_not_tested" /* NotTested */ : "" /* ASIS */;
    };
    SettingsPageComponent.prototype.getMailSettingsConfirmationMessage = function (smtpTestResult) {
        switch (smtpTestResult) {
            case "settings_mail_test_failed" /* Failed */:
                return 'settings_mail_test_failed_confirmation';
            case "settings_mail_not_tested" /* NotTested */:
                return 'settings_mail_not_tested_confirmation';
            case "" /* ASIS */:
            case "settings_mail_test_succeeded" /* Succeeded */:
                return null;
            default:
                throw new Error('Unknown SMTP test result: ' + smtpTestResult);
        }
    };
    SettingsPageComponent.prototype.saveSettings = function () {
        var _this = this;
        this.loading$.next(true);
        var payload = cloneDeep(this.settings);
        var selectedCalendar = this.excludedCalendars[this.selectedCalendarIndex];
        if (!this.isMultipleCalendarsEnabled && this.skippedDays.formGroup.dirty) {
            selectedCalendar.excludedDates = this.skippedDays.formGroup.get('date')
                .value
                .map(function (d) { return moment(d).toDate(); });
        }
        var updateCalendar$ = !this.skippedDays.formGroup.dirty || this.isMultipleCalendarsEnabled ?
            of(null) :
            this.settingService.updateCalendar(selectedCalendar);
        combineLatest([
            this.settingService
                .updateSettingsBulk(payload, {
                'General': this.general.formGroup.dirty,
                'Deployment': this.deployment.formGroup.dirty,
                'Mail': this.mail.formGroup.dirty,
                'Security': this.security.formGroup.dirty,
                'Scalability': this.scalability.formGroup.dirty,
            }),
            updateCalendar$,
        ])
            .pipe(switchMap(function () { return _this._appState.settings.fetch(); }), tap(function () {
            _this._snack.success(_this._translateService.instant('settings_save_success_message'));
            _this.general.formGroup.reset(_this.general.formGroup.value);
            _this.deployment.formGroup.reset(_this.deployment.formGroup.getRawValue());
            _this.mail.formGroup.reset(_this.mail.formGroup.value);
            _this.security.formGroup.reset(_this.security.formGroup.value);
            _this.scalability.formGroup.reset(_this.scalability.formGroup.getRawValue());
            if (!_this.isMultipleCalendarsEnabled) {
                _this.skippedDays.formGroup.reset(_this.skippedDays.formGroup.value);
            }
        }), switchMap(function () { return _this.settingService.getServerSettings(); }), finalize(function () { return _this.loading$.next(false); }))
            .subscribe(function (newSettings) { return _this.settings = newSettings; });
    };
    SettingsPageComponent.prototype.selectionChange = function (index) {
        this.selectedCalendarIndex = index;
    };
    SettingsPageComponent.prototype._checkMailSettings = function () {
        var _this = this;
        var mailSettingsConfirmationMessage = this.getMailSettingsConfirmationMessage(this._smtpTestResult);
        if (!this.mail.formGroup.dirty || mailSettingsConfirmationMessage == null) {
            this.saveSettings();
        }
        else {
            this._modalService.onConfirm(null, function () { return _this.saveSettings(); }, function () { }, this._translateService.instant(mailSettingsConfirmationMessage));
        }
    };
    return SettingsPageComponent;
}());
export { SettingsPageComponent };
